// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Data loading. Each file is fetched at most once and shared between views;
// the heavy geometry is fetched only by the view that draws it, so a reader who
// never opens the map never pays for three megabytes of polygons.

import type {
  Determination, OutcomePolygonRow, Jurisdiction, Series, AgreementsFile, Matter, Holder, Ratsib, Meta,
} from './types';

const BASE = `${import.meta.env.BASE_URL ?? '/'}data`.replace(/\/{2,}/g, '/');

const cache = new Map<string, Promise<unknown>>();

/**
 * One in-flight promise per file. Two views mounting at once — or a reader
 * switching views before the first load settles — must not start a second
 * download of the same three-megabyte layer.
 */
function load<T>(name: string): Promise<T> {
  const existing = cache.get(name) as Promise<T> | undefined;
  if (existing) return existing;
  const p = (async () => {
    const res = await fetch(`${BASE}/${name}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Could not load ${name} (HTTP ${res.status})`);
    return (await res.json()) as T;
  })().catch((err) => {
    // A failed load must not be cached, or the retry button can never succeed.
    cache.delete(name);
    throw err;
  });
  cache.set(name, p);
  return p;
}

export const loadMeta = () => load<Meta>('meta.json');
export const loadDeterminations = () => load<Determination[]>('determinations.json');
/** The per-determination tenure breakdown, shared by the map and the register. */
export const loadOutcomes = () => load<OutcomePolygonRow[]>('outcomes.json');
export const loadJurisdictions = () => load<Jurisdiction[]>('jurisdictions.json');
export const loadSeries = () => load<Series>('series.json');
export const loadAgreements = () => load<AgreementsFile>('agreements.json');
export const loadMatters = () => load<Matter[]>('matters.json');
export const loadHolders = () => load<Holder[]>('holders.json');
export const loadRatsib = () => load<Ratsib[]>('ratsib.json');

export const loadOutcomeGeometry = () => load<GeoJSON.FeatureCollection>('outcomes.geojson');
export const loadClaimGeometry = () => load<GeoJSON.FeatureCollection>('claims.geojson');
export const loadScheduleGeometry = () => load<GeoJSON.FeatureCollection>('schedule.geojson');
export const loadRatsibGeometry = () => load<GeoJSON.FeatureCollection>('ratsib.geojson');
export const loadAgreementGeometry = () => load<GeoJSON.FeatureCollection>('agreements.geojson');
export const loadStates = () => load<GeoJSON.FeatureCollection>('au-states.geojson');

/**
 * Meta and the determination register are loaded at boot because every view
 * needs them; `getMeta()` is the synchronous accessor for code that runs after
 * boot has already awaited them.
 */
let meta: Meta | null = null;
let determinations: Determination[] | null = null;
let byId: Map<string, Determination> | null = null;

export async function boot(): Promise<{ meta: Meta; determinations: Determination[] }> {
  const [m, d] = await Promise.all([loadMeta(), loadDeterminations()]);
  meta = m;
  determinations = d;
  byId = new Map(d.map((row) => [row.id, row]));
  return { meta: m, determinations: d };
}

export function getMeta(): Meta {
  if (!meta) throw new Error('meta requested before boot');
  return meta;
}

export function getDeterminations(): Determination[] {
  if (!determinations) throw new Error('determinations requested before boot');
  return determinations;
}

export function getDetermination(id: string): Determination | undefined {
  return byId?.get(id);
}
